import { db } from './firebase';
import { doc, setDoc, getDocs, collection, deleteDoc, getDoc, writeBatch, updateDoc } from 'firebase/firestore';
import { Project, AppConfig, CustomMethod } from '@/types';


// Refactored to separate customMethods into a subcollection for scalability
export const saveUserConfig = async (userId: string, config: AppConfig) => {
    // Destructure to exclude customMethods from the main config file
    // @ts-ignore - unused variable is intentional
    const { customMethods, ...restConfig } = config;

    const userRef = doc(db, 'users', userId, 'settings', 'config');
    await setDoc(userRef, restConfig, { merge: true });
};

export const loadUserConfig = async (userId: string): Promise<AppConfig | null> => {
    const userRef = doc(db, 'users', userId, 'settings', 'config');
    const methodsRef = collection(db, 'users', userId, 'custom_methods');

    try {
        const [configSnap, methodsSnap] = await Promise.all([
            getDoc(userRef),
            getDocs(methodsRef)
        ]);

        if (configSnap.exists()) {
            const configData = configSnap.data() as AppConfig;

            // Load methods from subcollection
            const loadedMethods: CustomMethod[] = methodsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as CustomMethod));

            return {
                ...configData,
                customMethods: loadedMethods
            };
        }
    } catch (error) {
        console.error("Error loading user config:", error);
    }

    return null;
};

// --- Custom Methods Subcollection Operations ---

export const addCustomMethodToFirestore = async (userId: string, method: CustomMethod) => {
    const methodRef = doc(db, 'users', userId, 'custom_methods', method.id);
    await setDoc(methodRef, method);
};

export const updateCustomMethodInFirestore = async (userId: string, method: CustomMethod) => {
    const methodRef = doc(db, 'users', userId, 'custom_methods', method.id);
    await setDoc(methodRef, method, { merge: true });
};

export const deleteCustomMethodFromFirestore = async (userId: string, methodId: string) => {
    const methodRef = doc(db, 'users', userId, 'custom_methods', methodId);
    await deleteDoc(methodRef);
};

// Projects
export const saveProjectToFirestore = async (userId: string, project: Project) => {
    const projectRef = doc(db, 'users', userId, 'projects', project.id);
    // Sanitize to remove undefined fields which Firebase rejects
    const cleanProject = JSON.parse(JSON.stringify(project));
    await setDoc(projectRef, cleanProject, { merge: true });
};

export const loadProjectsFromFirestore = async (userId: string): Promise<Project[]> => {
    const projectsRef = collection(db, 'users', userId, 'projects');
    const snapshot = await getDocs(projectsRef);
    return snapshot.docs.map(doc => doc.data() as Project);
};

export const deleteProjectFromFirestore = async (userId: string, projectId: string) => {
    const projectRef = doc(db, 'users', userId, 'projects', projectId);
    await deleteDoc(projectRef);
};

// Sync all projects (Useful for migration or full save)
export const syncAllProjects = async (userId: string, projects: Project[]) => {
    const batch = writeBatch(db);

    // Sanitize to remove undefined fields
    const cleanProjects = JSON.parse(JSON.stringify(projects));

    cleanProjects.forEach((project: Project) => {
        const projectRef = doc(db, 'users', userId, 'projects', project.id);
        batch.set(projectRef, project, { merge: true });
    });
    await batch.commit();
};

export const syncAppStateToFirestore = async (userId: string, projects: Project[], config: AppConfig) => {
    await saveUserConfig(userId, config);
    if (projects.length > 0) {
        await syncAllProjects(userId, projects);
    }
}

// --- Learned Elements (Global Knowledge Base) ---

export const saveLearnedElement = async (userId: string, label: string, category: string) => {
    const elementRef = doc(db, 'users', userId, 'learned_elements', label.toUpperCase());
    await setDoc(elementRef, {
        label: label.toUpperCase(),
        category,
        updatedAt: Date.now()
    });
};

export const loadLearnedElements = async (userId: string): Promise<Record<string, string>> => {
    const elementsRef = collection(db, 'users', userId, 'learned_elements');
    const snapshot = await getDocs(elementsRef);
    const learnedElements: Record<string, string> = {};
    snapshot.docs.forEach(doc => {
        const data = doc.data();
        learnedElements[doc.id] = data.category;
    });
    return learnedElements;
};

// --- User Tags ---

export const saveUserTagsToFirestore = async (userId: string, tags: string[]) => {
    const userRef = doc(db, 'users', userId, 'settings', 'tags');
    await setDoc(userRef, { tags, lastUpdated: Date.now() }, { merge: true });
};

export const loadUserTagsFromFirestore = async (userId: string): Promise<string[]> => {
    const userRef = doc(db, 'users', userId, 'settings', 'tags');
    const snap = await getDoc(userRef);
    if (snap.exists()) {
        return snap.data().tags || [];
    }
    return [];
};

export const deleteLearnedElement = async (userId: string, label: string) => {
    const elementRef = doc(db, 'users', userId, 'learned_elements', label.toUpperCase());
    await deleteDoc(elementRef);
};
